
import ApplicationGalleryTile from '@/components/inspiration/galleries/application_gallery_tile';
import PageLayout, { siteTitle } from "@/components/PageLayout";
import SearchBuilder from "@/components/search_builder.js";
import DropdownFilter from "@/components/dropdown_filter";
import PhotoFilterPillBar from "@/components/inspiration/photo_filter_pill_bar";
import { useState, useEffect, useContext } from "react";
import Qs from "qs";
import GalleryService from '@/lib/services/galleries';
import searchTranslator from "@/lib/search_translator.js";
import { loadConfig } from "@/lib/utils.js";
import Link from "next/link";
import SearchToggle from '@/components/inspiration/search_toggle';
import { is_mobile } from '@/lib/utils.js';
import InspirationBreadcrumbs from '@/components/inspiration/breadcrumbs';
import { GalleryCacheContext } from '@/lib/inspiration_cache_context.js';


export default function GalleriesSearchPage(results) {
  const {
    galleries,
    total_count,
    initialSearch,
    page_layout,
    favicon,
    stylesheet,
    cart_enabled,
    application_category_filters,
    space_filters,
    brand_filters,
    product_type_filters,
    next_page,
    contained_product_name,
    inspiration_display,
    mobile, 
    clientNavigation, 
    scroll
  } = results;

  const galleryCache = useContext(GalleryCacheContext);

  const [isLoading, setIsLoading] = useState(false);

  let galleryFilters = [
    [
      'photos_application_categories_id_eq',
      'Application Types',
      galleryCache.applicationCategoryFilters
    ],
    [
      'photos_application_spaces_id_eq',
      'Spaces',
      galleryCache.spaceFilters
    ],
    [
      'photos_product_hot_spots_product_tags_id_eq',
      'Product Types',
      galleryCache.productTypeFilters
    ],
  ];

  if (galleryCache.brandFilters) {
    galleryFilters.push([
      'company_id_eq',
      'Brand Filter',
      galleryCache.brandFilters
    ]);
  }


  /*
   * Populate the cache from the initial server-side props.
   *
   * If we arrived here because of clientNavigation, the cache
   * already contains the galleries that were loaded before the
   * user opened a gallery, so we don't replace them.
   */
  useEffect(() => {
    if (!galleryCache.items?.length) {
      galleryCache.setItems(galleries || []);
      galleryCache.setTotalCount(total_count || 0);
      galleryCache.setNextPage(next_page);
      galleryCache.setSearch(initialSearch || {});

      galleryCache.setApplicationCategoryFilters(
        application_category_filters || []
      );

      galleryCache.setSpaceFilters(
        space_filters || []
      );

      galleryCache.setProductTypeFilters(
        product_type_filters || []
      );

      galleryCache.setBrandFilters(
        brand_filters || []
      );
    }

    if (scroll) {
      window.scrollTo({top: scroll, behavior: 'instant'} )
    }
  }, []);


  /*
   * Perform a new gallery search.
   *
   * A new search replaces the cached galleries because the
   * existing collection belongs to the previous search.
   */
  const searchGalleries = (search) => {
    setIsLoading(true);

    fetch(
      "/lex-api/inspiration/galleries/all" +
        "?" +
        Qs.stringify(search, {
          arrayFormat: "brackets",
          encode: true
        })
    )
      .then((res) => res.json())
      .then((data) => {
        setIsLoading(false);

        galleryCache.setItems(data.galleries);
        galleryCache.setTotalCount(data.total_count);
        galleryCache.setNextPage(data.next_page);
        galleryCache.setSearch(search);

        galleryCache.setApplicationCategoryFilters(
          data.application_category_filters
        );

        galleryCache.setSpaceFilters(
          data.space_filters
        );

        galleryCache.setProductTypeFilters(
          data.product_type_filters
        );

        if (data.brand_filters) {
          galleryCache.setBrandFilters(data.brand_filters);
        }
      });
  };


  /*
   * Load the next page and append it to the cached collection.
   */
  const loadMoreGalleries = (currentSearch) => {
    setIsLoading(true);

    let search = {
      ...(currentSearch || {}),
      page: galleryCache.nextPage
    };

    fetch(
      "/lex-api/inspiration/galleries/all" +
        "?" +
        Qs.stringify(search, {
          arrayFormat: "brackets",
          encode: true
        })
    )
      .then((res) => res.json())
      .then((data) => {
        setIsLoading(false);

        galleryCache.appendGalleries(data.galleries);
        galleryCache.setNextPage(data.next_page);
      });
  };


  return (
    <PageLayout
      title="Inspiration Search"
      page_layout={page_layout}
      cart_enabled={cart_enabled}
      favicon={favicon}
      stylesheet={stylesheet}
      inspirationBreadcrumbs={
        <InspirationBreadcrumbs
          breadcrumbs_path={[
            {
              path: '/lex-cms/inspiration/galleries/index',
              name: 'Galleries'
            }
          ]}
        />
      }
    >
      <SearchBuilder
        initialSearch={initialSearch}
        useNextRouterForSearchUrl={true}
        search={searchGalleries}
      >
        {(searchState) => {
          return (
            <div className='lexcms-inspiration'>

              <div className='lexcms-inspiration-search-container'>

                <div className="lexcms-inspiration-search-header-text">
                  <h1>Inspirational Search</h1>
                  <p>
                    Discover awe-inspiring images from our projects.
                  </p>
                </div>

                <div className='lexcms-inspiration-sidebar-filters'>

                  {inspiration_display ==
                    'both_application_photos_and_galleries' &&
                    <SearchToggle
                      display={inspiration_display}
                      galleriesSelected={true}
                    />
                  }

                  <p className='lexcms-inspiration-search-refine-photos-count'>
                    Refine results:
                  </p>

                  {galleryFilters.map((d, i) => {
                    return (
                      <DropdownFilter
                        key={i}
                        searchKey={`q[${d[0]}]`}
                        className={"inspiration-search-sidebar-filter"}
                        searchState={searchState}
                        options={d[2]}
                        label={d[1]}
                        disableFirst={true}
                      />
                    );
                  })}

                  <Link href='/lex-cms/inspiration/highlights'>
                    <a
                      className='lexcms-regular-button lexcms-clear-photos-button'
                      onClick={() => {
                        searchGalleries({});
                        searchState.clearAll();
                      }}
                    >
                      Reset All Selections
                    </a>
                  </Link>

                </div>
              </div>


              <div className='lexcms-search-results app-gallery-container'>

                <div className='lexcms-filters-container lexcms-results-top'>

                  <PhotoFilterPillBar
                    searchState={searchState}
                    application_categories={
                      galleryCache.applicationCategoryFilters
                    }
                    space_filters={
                      galleryCache.spaceFilters
                    }
                    product_type_filters={
                      galleryCache.productTypeFilters
                    }
                    resourceName={'galleries'}
                    filters={galleryFilters}
                    total_count={galleryCache.totalCount}
                    searchGalleries={searchGalleries}
                    contained_product_name={contained_product_name}
                  />

                  <div className='lexcms-inspiration-search-sort-container'>

                    <DropdownFilter
                      searchKey={"q[s]"}
                      className={"inspiration-search-sort-filter"}
                      searchState={searchState}
                      options={[
                        {
                          value: 'created_at DESC',
                          label: 'Latest'
                        },
                        {
                          value: 'name ASC',
                          label: 'A-Z'
                        },
                        {
                          value: 'name DESC',
                          label: 'Z-A'
                        }
                      ]}
                      label="Sort"
                      disableFirst={true}
                    />

                  </div>
                </div>


                <div className='lexcms-result-container'>

                  <div className='app-gallery-list'>

                    {galleryCache.items?.map((d, i) => {
                      return (
                        <ApplicationGalleryTile
                          app_gallery={d}
                          key={d.id}
                          index={i}
                          mobile={mobile}
                        />
                      );
                    })}

                  </div>

                </div>


                {galleryCache.nextPage &&
                  <div className='galleries-remaining-link'>
                    <a
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        loadMoreGalleries(searchState.search)
                      }}
                    >
                      Load More Galleries
                    </a>
                  </div>
                }

              </div>
            </div>
          );
        }}
      </SearchBuilder>
    </PageLayout>
  );
}


export async function getServerSideProps(context) {
  let token = context?.req?.headers['lex-api-token'];

  let service = await GalleryService.build(
    token,
    context.req?.headers.host
  );

  let q = searchTranslator(context.query);

  let search = {
    initialSearch: q
  };

  let mobile = is_mobile(
    context.req?.headers['user-agent']
  );

  let pageConfig = loadConfig(service);


  /*
   * When returning from an individual gallery, the gallery
   * search results are already held in GalleryCacheContext.
   *
   * Don't make another API request for the first page.
   */

  if (context.query?.clientNavigation) {

    return {
      props: {
        ...pageConfig,
        ...search,
        mobile, 
        scroll: context.query?.scroll || null
      }
    };
  }


  let apiFriendlyQ = searchTranslator(
    context.query,
    true
  );

  let results = await service.index(apiFriendlyQ);

  return {
    props: {
      ...results,
      ...pageConfig,
      ...search,
      mobile
    }
  };
}

